import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import React, { useContext, useEffect, useState } from 'react';

import { axiosPostCall } from '../../api/axiosCall';
import {
  API_END_POINT,
  ROUTES,
  SOMETHING_WENT_WRONG,
} from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';

function Login() {
  const [loginForm] = Form.useForm();
  const { updateTokens, isAuthenticated, getFCMTokenForLogin } =
    useContext(AuthContext);

  // State management
  const [currentStep, setCurrentStep] = useState(1); // 1: Mobile, 2: OTP
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [mobileNumber, setMobileNumber] = useState('');

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Redirect to home if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      window.location.href = ROUTES.HOME;
    }
  }, [isAuthenticated]);

  // Send OTP function
  const sendOTP = async (mobileNo) => {
    try {
      setIsLoading(true);
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.SEND_LOGIN_OTP,
        header: {
          'Client-type': 'admin',
        },
        body: {
          mobileNo: parseInt(mobileNo),
        },
      });

      if (res?.hideErrorFromUser || res?.code === 200) {
        setCurrentStep(2);
        setCountdown(60); // 60 seconds countdown
        message.success('OTP sent successfully to your mobile number');
      } else {
        message.error(res?.error?.message || 'Failed to send OTP');
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  };

  // Login with OTP function
  const loginWithOTP = async (mobileNo, otp) => {
    try {
      setIsLoading(true);

      // Get FCM token for login API only
      const fcmToken = await getFCMTokenForLogin();
      console.log('FCM Token for login:', fcmToken);

      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.LOGIN,
        header: {
          'Client-type': 'admin',
        },
        body: {
          mobileNo: mobileNo,
          otp: otp,
          deviceId: fcmToken,
        },
      });

      if (res?.hideErrorFromUser && res?.data) {
        // Update context with new tokens (this will also update cookies)
        updateTokens(res.data.accessToken, res.data.refreshToken);

        message.success('Login successful!');
        loginForm.resetFields();
        setCurrentStep(1);
        setCountdown(0);
        window.open(ROUTES.HOME, '_self');
      } else {
        message.error(res?.error?.message || 'Invalid OTP');
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const onFinish = (values) => {
    if (currentStep === 1) {
      setMobileNumber(values.mobileNumber);
      sendOTP(values.mobileNumber);
    } else {
      loginWithOTP(mobileNumber, values.otp);
    }
  };

  // Handle form validation
  const handleValueChange = (changedValues, allValues) => {
    if (currentStep === 1) {
      setIsButtonEnabled(
        allValues.mobileNumber && allValues.mobileNumber.length === 10,
      );
    } else {
      setIsButtonEnabled(allValues.otp && allValues.otp.length === 6);
    }
  };

  // Resend OTP function
  const resendOTP = () => {
    if (countdown === 0) {
      sendOTP(mobileNumber);
    }
  };

  // Go back to mobile number step
  const goBack = () => {
    setCurrentStep(1);
    setCountdown(0);
    loginForm.resetFields();
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-6'>
      <div className='w-full max-w-md'>
        {/* Logo/Brand Section */}
        <div className='text-center mb-8'>
          <div className='w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow'>
            <img
              src='/png/megasun-logo.png'
              alt='MegaSun Logo'
              className='w-12 h-12 object-contain'
            />
          </div>
          <h1 className='text-2xl font-bold text-text mb-2'>
            Welcome to MegaSun
          </h1>
          <p className='text-muted'>Admin Portal</p>
        </div>

        {/* Login Card */}
        <div className='bg-surface rounded-xl shadow p-8 border border-border'>
          <div className='mb-6'>
            <h2 className='text-xl font-semibold text-text mb-2'>
              {currentStep === 1 ? 'Enter Mobile Number' : 'Enter OTP'}
            </h2>
            <p className='text-muted text-sm'>
              {currentStep === 1
                ? "We'll send you a verification code"
                : `We've sent a 6-digit code to ${mobileNumber}`}
            </p>
          </div>

          <Form
            form={loginForm}
            onFinish={onFinish}
            onValuesChange={handleValueChange}
            layout='vertical'
            className='space-y-6'
          >
            {currentStep === 1 ? (
              <Form.Item
                name='mobileNumber'
                rules={[
                  {
                    required: true,
                    message: 'Mobile number is required!',
                  },
                  {
                    pattern: /^[6-9]\d{9}$/,
                    message: 'Please enter a valid 10-digit mobile number!',
                  },
                ]}
                className='mb-6'
              >
                <Input
                  className='h-12 text-lg rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200'
                  placeholder='Enter your mobile number'
                  maxLength={10}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name='otp'
                rules={[
                  {
                    required: true,
                    message: 'OTP is required!',
                  },
                  {
                    pattern: /^\d{6}$/,
                    message: 'Please enter a valid 6-digit OTP!',
                  },
                ]}
                className='mb-6'
              >
                <Input
                  className='h-12 text-center text-2xl tracking-widest rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200'
                  placeholder='000000'
                  maxLength={6}
                />
              </Form.Item>
            )}

            <Button
              type='primary'
              htmlType='submit'
              loading={isLoading}
              disabled={!isButtonEnabled}
              className='w-full h-12 rounded-lg text-lg font-medium border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md transition-all duration-200'
            >
              {isLoading
                ? 'Processing...'
                : currentStep === 1
                  ? 'Send OTP'
                  : 'Verify & Login'}
            </Button>

            {currentStep === 2 && (
              <div className='text-center space-y-3'>
                <p className='text-muted text-sm'>
                  Didn't receive the code?{' '}
                  <button
                    type='button'
                    onClick={resendOTP}
                    disabled={countdown > 0}
                    className={`font-medium ${
                      countdown > 0
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-primary hover:text-primary-700'
                    }`}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </p>
                <button
                  type='button'
                  onClick={goBack}
                  className='text-muted hover:text-text text-sm font-medium'
                >
                  ← Change mobile number
                </button>
              </div>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
}

export default Login;
